import { useTranslation } from 'next-i18next';
import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { Button, IconButton } from '@restworld/ui-ds-base';

interface PayTransparencyBannerProps {
  onDismiss: () => void;
  onCtaClick: () => void;
}

/**
 * Floating pay-transparency reminder banner.
 *
 * Layout and content follow the Figma design; the styling uses the Design System
 * "warning" tokens (orange-50 surface, orange-700 icon/text) rather than reusing
 * the Toast component. ui-ds-base has no standalone Alert, hence the plain div card.
 * Fixed at the bottom of the viewport, below the side rail (z-50) / mobile header,
 * clearing the 88px desktop rail with an xl inset.
 * Design: https://www.figma.com/design/BJulKQsT0epYSxIcD7LXwK/Missing-Gross-salary-alert?node-id=11-1321
 */
export function PayTransparencyBanner({ onDismiss, onCtaClick }: PayTransparencyBannerProps) {
  const { t } = useTranslation('common');

  return (
    <div className="fixed z-40 bottom-2 left-2 right-2 xl:left-[104px]">
      {/* DS "warning" surface tokens: orange-50 bg + orange-700 ink, radius-md (8px),
          16px gap/padding, drop shadow per Figma. */}
      <div className="relative mx-auto w-auto max-w-2xl flex flex-col gap-4 bg-orange-50 text-orange-700 rounded-lg shadow-toast-dark p-4">
        <div className="flex items-center justify-between w-full">
          <InformationCircleIcon className="w-6 h-6 text-orange-700 shrink-0" />
          <IconButton
            variant="warning"
            size="sm"
            Icon={XMarkIcon}
            ariaLabel={t('pay_transparency_banner_dismiss_aria_label')}
            onClick={onDismiss}
          />
        </div>
        <p className="text-base font-medium leading-normal">{t('pay_transparency_banner_message')}</p>
        <div className="flex">
          <Button variant="secondary-warning" size="sm" onClick={onCtaClick}>
            {t('pay_transparency_banner_cta_label')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PayTransparencyBanner;
